import { type ReactNode } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/web/components/primitives/dialog";
import { TooltipContent, type TooltipColor } from "@/web/components/primitives/tooltip";

/**
 * Shared chrome for ack/tour dialogs. Keeps the modal styling and dismissal
 * lockdown (no close button, no escape, no outside-click) in one place so the
 * orientation tour and standalone ack modals can't drift visually.
 */
export function AckDialogShell({
  title,
  body,
  footer,
}: {
  title: ReactNode;
  body: ReactNode;
  footer: ReactNode;
}) {
  return (
    <Dialog open>
      <DialogContent
        showCloseButton={false}
        className="w-[90%] max-w-md p-6"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="withus-strong text-center text-2xl text-withus-red md:text-4xl">
            {title}
          </DialogTitle>
          <DialogDescription hidden />
        </DialogHeader>
        <div>{body}</div>
        <DialogFooter>{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Shared chrome for ack/tour tooltips. The trigger lives outside (each caller
 * wires Radix's `TooltipTrigger` differently — `asChild` on a forwardRef'd
 * anchor for ack-tooltips, a ghost rect for tour-tooltips).
 */
export function AckTooltipShell({
  side,
  color,
  title,
  body,
  footer,
}: {
  side: "top" | "bottom" | "left" | "right";
  color?: TooltipColor;
  title: ReactNode;
  body: ReactNode;
  footer: ReactNode;
}) {
  return (
    <TooltipContent
      side={side}
      sideOffset={8}
      color={color}
      className="p-0"
      onPointerDownOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <div className="flex w-72 flex-col gap-3 p-3 text-left">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold">{title}</p>
          <div className="text-sm">{body}</div>
        </div>
        <div className="flex flex-row-reverse items-center justify-between gap-2">{footer}</div>
      </div>
    </TooltipContent>
  );
}
